import { GUEST_PATH_SILENCE_MS } from '@playcastdotio/PeerCore';

/**
 * What the host does after guest stats go stale while ICE still looks connected.
 *
 * The first step asks the guest, over the data channel, to restart. That ask is useless
 * when the data channel is the thing that died. If the guest has not sent a new offer
 * or fresh stats before the silence window ends, the host restarts ICE itself.
 * A player whose ICE is `disconnected` or `failed` is only held open and then torn
 * down — that handler does not restart ICE. `checking` is left alone because a
 * negotiation is already in progress.
 */
export class GuestFeedbackPathRestart {
  private readonly askedAtMs = new Map<string, number>();

  noteFeedbackLost(peerId: string, nowMs: number): void {
    this.askedAtMs.set(peerId, nowMs);
  }

  /** Fresh guest stats, or a guest offer that arrived after the ask. */
  noteGuestAlive(peerId: string): void {
    this.askedAtMs.delete(peerId);
  }

  /**
   * True once, when the silence window has elapsed and the guest has not taken the path back.
   * Connected, completed, disconnected, and failed all need this restart. The Android
   * stall stayed `connected`. A refresh moves ICE to `disconnected` inside the same
   * window, and skipping that state is how the picture stayed down.
   */
  consumeRestart(peerId: string, nowMs: number, iceState: string | undefined, remoteOfferAtMs: number): boolean {
    const askedAt = this.askedAtMs.get(peerId);
    if (askedAt === undefined || nowMs < askedAt + GUEST_PATH_SILENCE_MS) return false;
    this.askedAtMs.delete(peerId);
    if (remoteOfferAtMs > askedAt) return false;
    return iceState === undefined
      || iceState === 'connected'
      || iceState === 'completed'
      || iceState === 'disconnected'
      || iceState === 'failed';
  }

  clear(peerId: string): void {
    this.askedAtMs.delete(peerId);
  }

  clearAll(): void {
    this.askedAtMs.clear();
  }
}

export const HOST_PATH_RESTART_DELAY_MS = GUEST_PATH_SILENCE_MS;
